import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Activity, Eye, Trash2, Edit, Plus, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminActivityLog() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [pagination.page, entityFilter, actionFilter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', pagination.page.toString());
      if (entityFilter) params.set('entity_type', entityFilter);
      if (actionFilter) params.set('action', actionFilter);
      
      const res = await axios.get(`${API}/admin/audit-logs?${params.toString()}`);
      setLogs(res.data.logs);
      setPagination({ page: res.data.page, pages: res.data.pages, total: res.data.total });
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/audit-logs/stats`);
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <Plus className="h-4 w-4 text-green-500" />;
      case 'update': return <Edit className="h-4 w-4 text-blue-500" />;
      case 'delete': return <Trash2 className="h-4 w-4 text-red-500" />;
      case 'status_change': return <RefreshCw className="h-4 w-4 text-yellow-500" />;
      default: return <Activity className="h-4 w-4 text-zinc-500" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create': return 'bg-green-500/20 text-green-500';
      case 'update': return 'bg-blue-500/20 text-blue-500';
      case 'delete': return 'bg-red-500/20 text-red-500';
      case 'status_change': return 'bg-yellow-500/20 text-yellow-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  const getEntityColor = (entity) => {
    switch (entity) {
      case 'order': return 'bg-purple-500/20 text-purple-500';
      case 'product': return 'bg-[#78BE20]/20 text-[#78BE20]';
      case 'user': return 'bg-blue-500/20 text-blue-500';
      case 'invoice': return 'bg-orange-500/20 text-orange-500';
      default: return 'bg-zinc-500/20 text-zinc-500';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const openDetail = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  return (
    <AdminLayout>
      <div data-testid="admin-activity-log">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black text-white uppercase tracking-tight">
              Activity Log
            </h1>
            <p className="text-zinc-400 text-sm mt-1">Track all admin actions and changes</p>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#18181b] border border-zinc-800 rounded-sm p-4">
              <p className="text-zinc-400 text-xs uppercase">Total Actions</p>
              <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
            </div>
            <div className="bg-[#18181b] border border-zinc-800 rounded-sm p-4">
              <p className="text-zinc-400 text-xs uppercase">Last 24 Hours</p>
              <p className="text-2xl font-bold text-[#78BE20] mt-1">{stats.last_24_hours}</p>
            </div>
            <div className="bg-[#18181b] border border-zinc-800 rounded-sm p-4">
              <p className="text-zinc-400 text-xs uppercase">Updates</p>
              <p className="text-2xl font-bold text-blue-500 mt-1">{stats.by_action?.update || 0}</p>
            </div>
            <div className="bg-[#18181b] border border-zinc-800 rounded-sm p-4">
              <p className="text-zinc-400 text-xs uppercase">Deletions</p>
              <p className="text-2xl font-bold text-red-500 mt-1">{stats.by_action?.delete || 0}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Select value={entityFilter || "all"} onValueChange={(v) => { setEntityFilter(v === 'all' ? '' : v); setPagination(p => ({...p, page: 1})); }}>
            <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="All Entities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Entities</SelectItem>
              <SelectItem value="order">Orders</SelectItem>
              <SelectItem value="product">Products</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="invoice">Invoices</SelectItem>
              <SelectItem value="purchase_order">Purchase Orders</SelectItem>
              <SelectItem value="category">Categories</SelectItem>
            </SelectContent>
          </Select>

          <Select value={actionFilter || "all"} onValueChange={(v) => { setActionFilter(v === 'all' ? '' : v); setPagination(p => ({...p, page: 1})); }}>
            <SelectTrigger className="w-48 bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="update">Update</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="status_change">Status Change</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        <div className="bg-[#18181b] border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-zinc-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Entity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Admin</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-zinc-400 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-4 py-4">
                      <div className="h-8 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                    No activity logs found
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} className="hover:bg-zinc-900/50">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {getActionIcon(log.action)}
                        <Badge className={getActionColor(log.action)}>{log.action}</Badge>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <Badge className={getEntityColor(log.entity_type)}>{log.entity_type}</Badge>
                      {log.entity_name && (
                        <p className="text-xs text-zinc-500 mt-1">{log.entity_name}</p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-white text-sm truncate max-w-xs">{log.description}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-zinc-400 text-sm">{log.admin_email}</p>
                    </td>
                    <td className="px-4 py-4 text-zinc-400 text-sm">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      {Object.keys(log.changes || {}).length > 0 && (
                        <Button variant="ghost" size="icon" onClick={() => openDetail(log)} className="text-zinc-400 hover:text-white">
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button
              variant="outline"
              size="icon"
              disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              className="border-zinc-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-zinc-400 px-4">
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="icon"
              disabled={pagination.page >= pagination.pages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              className="border-zinc-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Change Details</DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4 mt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Action:</span>
                  <Badge className={getActionColor(selectedLog.action)}>{selectedLog.action}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Entity:</span>
                  <span className="text-white">{selectedLog.entity_type} - {selectedLog.entity_name || selectedLog.entity_id.slice(0, 8)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Admin:</span>
                  <span className="text-white">{selectedLog.admin_email}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Time:</span>
                  <span className="text-white">{formatDate(selectedLog.created_at)}</span>
                </div>
                
                {Object.keys(selectedLog.changes || {}).length > 0 && (
                  <div className="mt-4">
                    <p className="text-zinc-400 text-sm mb-2">Changes Made:</p>
                    <div className="bg-zinc-800 rounded-lg p-4 space-y-3">
                      {Object.entries(selectedLog.changes).map(([field, change]) => (
                        <div key={field} className="text-sm">
                          <p className="text-zinc-400 font-medium capitalize">{field.replace(/_/g, ' ')}</p>
                          <div className="flex gap-2 mt-1">
                            <span className="text-red-400 line-through">{JSON.stringify(change.old)}</span>
                            <span className="text-zinc-500">→</span>
                            <span className="text-green-400">{JSON.stringify(change.new)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
